import React, { useState, useEffect } from 'react';
import { Search, FileSearch, BarChart3, Shield, Youtube, Globe, CheckCircle } from 'lucide-react';

const loadingSteps = [
  {
    icon: Search,
    title: "Finding venue on Google...",
    description: "Locating official Google Business listing"
  },
  {
    icon: FileSearch,
    title: "Analyzing reviews...",
    description: "Processing all available reviews for insights"
  },
  {
    icon: BarChart3,
    title: "Calculating trust scores...",
    description: "Evaluating sentiment and authenticity patterns"
  },
  {
    icon: Shield,
    title: "Detecting red flags...",
    description: "Scanning for hidden concerns in reviews"
  },
  {
    icon: Youtube,
    title: "Finding venue videos...",
    description: "Searching YouTube for walkthrough videos"
  },
  {
    icon: Globe,
    title: "Scraping venue website...",
    description: "Extracting capacity, rooms, and amenities info"
  },
  {
    icon: BarChart3,
    title: "Extracting staff mentions...",
    description: "Identifying key staff members and service highlights"
  },
  {
    icon: CheckCircle,
    title: "Generating report...",
    description: "Compiling comprehensive venue intelligence"
  }
];

const SearchLoadingState = ({ venueName }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  useEffect(() => {
    // Rotate through steps every 5-7 seconds
    const interval = setInterval(() => {
      setCurrentStep(prev => {
        const newStep = (prev + 1) % loadingSteps.length;
        // Mark previous step as completed
        setCompletedSteps(completed => {
          if (!completed.includes(prev)) {
            return [...completed, prev];
          }
          return completed;
        });
        return newStep;
      });
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  const CurrentIcon = loadingSteps[currentStep].icon;

  return (
    <div className="fixed inset-0 bg-[#FDFCF8]/95 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        {/* Main Loading Card */}
        <div className="bg-white border border-stone-200 rounded-2xl p-8 shadow-lg">
          {/* Venue Name */}
          <div className="text-center mb-8">
            <h2 className="font-serif text-xl font-medium text-stone-900 mb-2">
              Generating Report
            </h2>
            <p className="text-amber-600 font-medium">{venueName}</p>
          </div>

          {/* Current Step */}
          <div className="flex items-center gap-4 mb-8 p-4 bg-stone-50 rounded-xl">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <CurrentIcon className="w-6 h-6 text-amber-600 animate-pulse" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-stone-900">{loadingSteps[currentStep].title}</p>
              <p className="text-sm text-stone-500">{loadingSteps[currentStep].description}</p>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="space-y-2">
            {loadingSteps.map((step, idx) => {
              const StepIcon = step.icon;
              const isCompleted = completedSteps.includes(idx);
              const isCurrent = idx === currentStep;
              
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                    isCurrent 
                      ? 'bg-amber-50 border border-amber-200' 
                      : isCompleted 
                        ? 'bg-green-50' 
                        : 'opacity-50'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    isCompleted 
                      ? 'bg-green-500' 
                      : isCurrent 
                        ? 'bg-amber-500' 
                        : 'bg-stone-200'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4 text-white" />
                    ) : (
                      <StepIcon className={`w-3 h-3 ${isCurrent ? 'text-white' : 'text-stone-400'}`} />
                    )}
                  </div>
                  <span className={`text-sm ${
                    isCompleted 
                      ? 'text-green-700' 
                      : isCurrent 
                        ? 'text-amber-700 font-medium' 
                        : 'text-stone-400'
                  }`}>
                    {step.title.replace('...', '')}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Footer Note */}
          <div className="mt-8 pt-6 border-t border-stone-200 text-center">
            <p className="text-xs text-stone-500">
              This usually takes 50-60 seconds. We&apos;re analyzing all available data to give you the most comprehensive report.
            </p>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex items-center justify-center gap-4 mt-6 text-xs text-stone-500">
          <span className="flex items-center gap-1">
            <Shield className="w-3 h-3" />
            No paid rankings
          </span>
          <span className="flex items-center gap-1">
            <BarChart3 className="w-3 h-3" />
            Data-driven insights
          </span>
        </div>
      </div>
    </div>
  );
};

export default SearchLoadingState;
